import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useMutation, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import CreateCompanyModal from "@/components/modals/create-company-modal";
import CreateContactModal from "@/components/modals/create-contact-modal";
import EditCompanyModal from "@/components/modals/edit-company-modal";
import NotesDialog from "@/components/notes/notes-dialog";
import FMCSACarriersBulkModal from "@/components/modals/fmcsa-carriers-bulk-modal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, Truck, Edit, Eye, Search, FileText, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLocation, Link } from "wouter";
import { formatPhoneNumber } from "@/lib/utils";

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  company: 300,
  type: 140,
  dotNumber: 150,
  phone: 150,
  carriers: 100,
  openWos: 100,
  actions: 120,
};

export default function Companies() {
  usePageTitle("Companies");
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [location, setLocation] = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 250);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [showFMCSABulkModal, setShowFMCSABulkModal] = useState(false);
  const [newlyCreatedCompany, setNewlyCreatedCompany] = useState<any>(null);
  const [pendingContactCompany, setPendingContactCompany] = useState<{ id: number; name: string } | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(DEFAULT_COLUMN_WIDTHS);
  const [nameSort, setNameSort] = useState<"asc" | "desc" | "none">("none");

  const cycleNameSort = () =>
    setNameSort((s) => (s === "none" ? "asc" : s === "asc" ? "desc" : "none"));

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const {
    data: companiesInfinite,
    isLoading: companiesLoading,
    fetchNextPage: companiesFetchNextPage,
    hasNextPage: companiesHasNextPage,
    isFetchingNextPage: companiesIsFetchingNextPage,
  } = useInfiniteQuery<{ data: any[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/companies", { search: debouncedSearch, type: typeFilter, status: statusFilter }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (typeFilter !== 'all') qs.set('type', typeFilter);
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/companies${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnMount: "always",
  });

  const companies = useMemo(() =>
    companiesInfinite?.pages.flatMap(p => p.data) ?? [],
    [companiesInfinite]
  );
  const companiesTotalCount = companiesInfinite?.pages[0]?.totalCount ?? companies.length;

  useEffect(() => {
    if (!companiesLoading && companies) {
      const params = new URLSearchParams(window.location.search);
      const companyId = params.get('id');
      if (companyId) {
        setLocation(`/companies/${companyId}`);
      }
    }
  }, [companies, companiesLoading, setLocation]);

  const handleColumnResize = useCallback((column: string, startX: number, startWidth: number) => {
    const onMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;
      setColumnWidths(prev => ({
        ...prev,
        [column]: Math.max(80, startWidth + delta),
      }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const createCompanyMutation = useMutation({
    mutationFn: async ({ companyData, officersToCreate, hasDotData }: { companyData: any; officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }>; hasDotData?: boolean }) => {
      const response = await apiRequest("POST", "/api/companies", companyData);
      const newCompany = await response.json();
      if (officersToCreate && officersToCreate.length > 0) {
        for (let i = 0; i < officersToCreate.length; i++) {
          const officer = officersToCreate[i];
          const contactData = {
            firstName: officer.firstName,
            lastName: officer.lastName,
            email: officer.email || undefined,
          };
          const contactResponse = await apiRequest("POST", "/api/contacts", contactData);
          const newContact = await contactResponse.json();
          await apiRequest("POST", `/api/contacts/${newContact.id}/companies`, {
            companyId: newCompany.id,
            relationshipType: "owner",
            isPrimary: i === 0,
          });
        }
      }
      return { ...newCompany, hasDotData, type: companyData.type };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      const officerCount = variables.officersToCreate?.length || 0;
      toast({
        title: "Success",
        description: officerCount > 0
          ? `Company created successfully with ${officerCount} officer contact(s)`
          : "Company created successfully",
      });
      setShowCreateModal(false);
      if (result.hasDotData && result.type !== "service_bureau" && (result.dotProfile?.dotNumber || result.dotNumber)) {
        setNewlyCreatedCompany(result);
        setShowFMCSABulkModal(true);
      } else {
        setPendingContactCompany({ id: result.id, name: result.name });
      }
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({ title: "Unauthorized", description: "You are logged out. Logging in again...", variant: "destructive" });
        setTimeout(() => { window.location.href = "/api/login"; }, 500);
        return;
      }
      toast({ title: "Error", description: "Failed to create company", variant: "destructive" });
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async ({ companyData, officersToCreate }: { companyData: any; officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }> }) => {
      await apiRequest("PUT", `/api/companies/${selectedCompany.id}`, companyData);
      if (officersToCreate && officersToCreate.length > 0) {
        for (const officer of officersToCreate) {
          const contactData = {
            firstName: officer.firstName,
            lastName: officer.lastName,
            email: officer.email || undefined,
          };
          const contactResponse = await apiRequest("POST", "/api/contacts", contactData);
          const newContact = await contactResponse.json();
          await apiRequest("POST", `/api/contacts/${newContact.id}/companies`, {
            companyId: selectedCompany.id,
            relationshipType: "owner",
          });
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      const officerCount = variables.officersToCreate?.length || 0;
      toast({
        title: "Success",
        description: officerCount > 0
          ? `Company updated successfully with ${officerCount} new contact(s) added`
          : "Company updated successfully",
      });
      setShowEditModal(false);
      setSelectedCompany(null);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({ title: "Unauthorized", description: "You are logged out. Logging in again...", variant: "destructive" });
        setTimeout(() => { window.location.href = "/api/login"; }, 500);
        return;
      }
      toast({ title: "Error", description: "Failed to update company", variant: "destructive" });
    },
  });

  const createContactForCompanyMutation = useMutation({
    mutationFn: async (contactData: any) => {
      const response = await apiRequest("POST", "/api/contacts", contactData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      toast({ title: "Success", description: "Contact created successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create contact", variant: "destructive" });
    },
  });

  if (isLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  const handleCreateCompany = (companyData: any, officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }>, hasDotData?: boolean) => {
    createCompanyMutation.mutate({ companyData, officersToCreate, hasDotData });
  };

  const handleUpdateCompany = (companyData: any, officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }>) => {
    updateCompanyMutation.mutate({ companyData, officersToCreate });
  };

  const handleContactForCompany = (contactData: any) => {
    const company = pendingContactCompany;
    createContactForCompanyMutation.mutate(contactData, {
      onSuccess: () => {
        setPendingContactCompany(null);
        setLocation(`/companies/${company!.id}`);
      },
    });
  };

  const handleSkipContact = () => {
    const company = pendingContactCompany;
    setPendingContactCompany(null);
    if (company) {
      setLocation(`/companies/${company.id}`);
    }
  };

  const handleEditCompany = (company: any) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleViewCompany = (company: any) => {
    setLocation(`/companies/${company.id}`);
  };

  const formatCompanyType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case "service_bureau": return "bg-blue-100 text-blue-800";
      case "direct_client": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "client": return "bg-green-100 text-green-800";
      case "lead": return "bg-blue-100 text-blue-800";
      case "inquiry": return "bg-purple-100 text-purple-800";
      case "blocked": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Server-side filtering handles type and status filters; copy to allow safe sort
  const filteredCompanies = [...companies] as any[];

  if (nameSort !== "none") {
    filteredCompanies.sort((a: any, b: any) =>
      nameSort === "asc"
        ? (a.name ?? "").localeCompare(b.name ?? "")
        : (b.name ?? "").localeCompare(a.name ?? "")
    );
  }

  const ResizeHandle = ({ column }: { column: string }) => (
    <div
      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize group-hover:bg-blue-300 hover:bg-blue-400 transition-colors"
      onMouseDown={(e) => {
        e.preventDefault();
        handleColumnResize(column, e.clientX, columnWidths[column]);
      }}
    />
  );

  return (
    <ResponsiveLayout title="Companies" subtitle="Manage service bureaus and direct clients" currentPage="companies">
      <div className="space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Companies</h2>
            <p className="text-sm text-gray-600">Manage service bureaus and direct clients</p>
          </div>
          {user?.role === 'admin' && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search companies, DOT numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Company Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="service_bureau">Service Bureau</SelectItem>
              <SelectItem value="direct_client">Direct Client</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active (Inquiry + Lead + Client)</SelectItem>
              <SelectItem value="inquiry">Inquiry</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {companiesLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading companies...</p>
          </div>
        ) : !companies || (companies as any[]).length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No companies found</p>
                {user?.role === 'admin' && (
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Company
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="table-fixed w-full" style={{ minWidth: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}>
                  <colgroup>
                    {Object.entries(columnWidths).map(([col, width]) => (
                      <col key={col} style={{ width }} />
                    ))}
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      {([
                        { key: "company", label: "Company" },
                        { key: "type", label: "Type" },
                        { key: "dotNumber", label: "DOT Number" },
                        { key: "phone", label: "Phone" },
                        { key: "carriers", label: "Carriers" },
                        { key: "openWos", label: "Open WOs" },
                        { key: "actions", label: "Actions" },
                      ] as const).map(({ key, label }) => (
                        <th
                          key={key}
                          className="group px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative select-none"
                          style={{ width: columnWidths[key] }}
                        >
                          {key === "company" ? (
                            <button
                              onClick={cycleNameSort}
                              className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                            >
                              {label}
                              {nameSort === "asc" ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : nameSort === "desc" ? (
                                <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 opacity-40" />
                              )}
                            </button>
                          ) : (
                            label
                          )}
                          <ResizeHandle column={key} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCompanies.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                          No companies match the current filters
                        </td>
                      </tr>
                    ) : filteredCompanies.map((company: any) => (
                      <tr key={company.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <div className="flex items-center min-w-0">
                            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                              <Building2 className="w-5 h-5 text-primary" />
                            </div>
                            <div className="ml-3 min-w-0">
                              <button
                                onClick={() => handleViewCompany(company)}
                                className="text-sm font-medium text-primary hover:text-primary/80 hover:underline text-left truncate block max-w-full"
                                data-testid={`link-company-${company.id}`}
                              >
                                {company.name}
                              </button>
                              <div className="text-sm text-gray-500 truncate">{company.email || "No email"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <div className="flex flex-col gap-1">
                            <Badge className={`${getCompanyTypeColor(company.type)} text-xs whitespace-nowrap w-fit`}>
                              {formatCompanyType(company.type)}
                            </Badge>
                            {company.status && (
                              <Badge className={`${getStatusColor(company.status)} text-xs whitespace-nowrap w-fit`}>
                                {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <div className="text-sm text-gray-900 truncate">
                            {company.dotProfile?.dotNumber || "Not set"}
                          </div>
                          {company.inactiveDotNumbers && (
                            <div className="text-sm text-gray-500 truncate">
                              Inactive: {company.inactiveDotNumbers}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" style={{ overflow: 'hidden' }}>
                          <span className="truncate block">{formatPhoneNumber(company.phone) || "Not set"}</span>
                        </td>
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <Link
                            href={`/carriers?companyId=${company.id}`}
                            className="flex items-center text-sm text-primary hover:text-primary/80 hover:underline"
                            data-testid={`link-carriers-${company.id}`}
                          >
                            <Truck className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span>{company.carrierCount ?? 0}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <Link
                            href={`/work-orders?companyId=${company.id}&status=open`}
                            className="flex items-center text-sm text-primary hover:text-primary/80 hover:underline"
                            data-testid={`link-open-work-orders-${company.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span>{company.openWorkOrderCount ?? 0}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-4" style={{ overflow: 'hidden' }}>
                          <div className="flex items-center justify-end gap-1">
                            <NotesDialog
                              entityType="company"
                              entityId={company.id.toString()}
                              entityName={company.name}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCompany(company)}
                              className="h-8 w-8 p-0"
                              title="Edit Company"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCompany(company)}
                              className="h-8 w-8 p-0"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {showCreateModal && (
        <CreateCompanyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateCompany}
          isLoading={createCompanyMutation.isPending}
        />
      )}

      {showEditModal && selectedCompany && (
        <EditCompanyModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCompany(null);
          }}
          onSubmit={handleUpdateCompany}
          isLoading={updateCompanyMutation.isPending}
          company={selectedCompany}
        />
      )}

      <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {companies.length} of {companiesTotalCount}
        </p>
        {!companiesLoading && companiesHasNextPage && (
          <Button variant="outline" onClick={() => companiesFetchNextPage()} disabled={companiesIsFetchingNextPage}
            aria-label="Load more companies" aria-busy={companiesIsFetchingNextPage}>
            {companiesIsFetchingNextPage ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : "Load more"}
          </Button>
        )}
      </div>

      {pendingContactCompany && (
        <CreateContactModal
          open={!!pendingContactCompany}
          onClose={handleSkipContact}
          onSubmit={handleContactForCompany}
          isLoading={createContactForCompanyMutation.isPending}
          defaultCompany={pendingContactCompany}
        />
      )}

      {showFMCSABulkModal && newlyCreatedCompany && (
        <FMCSACarriersBulkModal
          open={showFMCSABulkModal}
          onClose={() => {
            const companyId = newlyCreatedCompany.id;
            setShowFMCSABulkModal(false);
            setNewlyCreatedCompany(null);
            setLocation(`/companies/${companyId}`);
          }}
          companyId={newlyCreatedCompany.id}
          companyName={newlyCreatedCompany.name}
          dotNumber={newlyCreatedCompany.dotProfile?.dotNumber}
          existingCarriers={[]}
          onSuccess={() => {
            const companyId = newlyCreatedCompany.id;
            queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
            setShowFMCSABulkModal(false);
            setNewlyCreatedCompany(null);
            toast({ title: "Success", description: "Carriers added from FMCSA successfully" });
            setLocation(`/companies/${companyId}`);
          }}
        />
      )}
    </ResponsiveLayout>
  );
}
